<template>
    <div class="employee-registry-page">
        <header class="employee-registry-page__header">
            <div>
                <h1>Сотрудники</h1>
                <p>
                    Единый реестр действующих и бывших сотрудников,
                    включая карточки без учётной записи Аргуса.
                </p>
            </div>
            <div class="employee-registry-page__actions">
                <b-button
                    v-if="permissions.can_manage_employees"
                    size="sm"
                    variant="success"
                    @click="openCreateEmployee"
                >
                    <i class="fas fa-user-plus mr-1"></i>
                    Создать сотрудника
                </b-button>
                <b-button
                    v-if="permissions.can_manage_employees"
                    size="sm"
                    :variant="mergeReviewCount > 0 ? 'warning' : 'outline-warning'"
                    :to="{name: 'employee-merge-review'}"
                    title="Проверить и объединить дубли карточек сотрудников"
                >
                    <i class="fas fa-code-branch mr-1" aria-hidden="true"></i>
                    Требует сверки
                    <b-badge v-if="mergeReviewCount > 0" variant="danger" pill class="ml-2"
                        :aria-label="'Открытых групп сверки: ' + mergeReviewCount">{{ mergeReviewCount }}</b-badge>
                    <span v-else-if="mergeReviewCountError" class="ml-1" title="Не удалось обновить счётчик сверки">!</span>
                </b-button>
                <input
                    ref="googleContactsInput"
                    type="file"
                    accept=".csv,text/csv"
                    class="d-none"
                    @change="onGoogleContactsFileSelected"
                >
                <b-button
                    v-if="permissions.can_import && permissions.can_manage_employees"
                    size="sm"
                    variant="outline-primary"
                    :disabled="importEmployeesLoading"
                    @click="$refs.googleContactsInput.click()"
                >
                    <b-spinner
                        v-if="importEmployeesLoading"
                        small
                        class="mr-1"
                    ></b-spinner>
                    <i v-else class="fas fa-file-import mr-1"></i>
                    Импорт Google CSV
                </b-button>
                <b-button
                    v-if="permissions.can_import"
                    size="sm"
                    variant="outline-primary"
                    :to="{name: 'employee-one-c-import'}"
                    title="Импорт сотрудников из 1С"
                >
                    <i class="fas fa-file-import mr-1" aria-hidden="true"></i>
                    Импорт 1С
                </b-button>
                <b-dropdown
                    v-if="permissions.can_import"
                    size="sm"
                    right
                    variant="outline-primary"
                    :disabled="exporting || !filteredEmployees.length"
                >
                    <template v-slot:button-content>
                        <b-spinner v-if="exporting" small class="mr-1"></b-spinner>
                        <i v-else class="fas fa-file-export mr-1"></i>
                        Экспорт CSV
                    </template>
                    <b-dropdown-item
                        :disabled="!selectedEmployeeIds.length"
                        @click="exportEmployees(selectedEmployeeIds)"
                    >
                        Выбранные ({{ selectedEmployeeIds.length }})
                    </b-dropdown-item>
                    <b-dropdown-item @click="exportEmployees(filteredEmployeeIds)">
                        Текущая выборка ({{ filteredEmployeeIds.length }})
                    </b-dropdown-item>
                </b-dropdown>
                <b-button
                    v-if="permissions.can_settings"
                    size="sm"
                    variant="outline-secondary"
                    :to="{name: 'employees-workforce-settings'}"
                    title="Настройки модуля «Сотрудники»"
                >
                    <i class="fas fa-cog mr-1" aria-hidden="true"></i>
                    Настройки
                </b-button>
            </div>
        </header>

        <p v-if="!loading && !errorMessage && !permissions.can_view_full_registry" class="text-muted">
            Показаны действующие сотрудники, к которым у вас есть доступ
        </p>
        <div v-if="permissions.can_view_full_registry" class="employee-registry-page__summary">
            <button
                v-for="item in statusQuickFilters"
                :key="item.value"
                type="button"
                :class="[
                    'employee-registry-status',
                    {'employee-registry-status--active': filters.status === item.value}
                ]"
                @click="applyStatusFilter(item.value)"
            >
                <span>{{ item.text }}</span>
                <strong>{{ totals[item.totalKey] || 0 }}</strong>
            </button>
        </div>

        <section class="employee-registry-page__filters">
            <b-input-group size="sm" class="employee-registry-page__search">
                <b-input-group-prepend is-text>
                    <i class="fas fa-search"></i>
                </b-input-group-prepend>
                <b-form-input
                    v-model.trim="filters.query"
                    type="search"
                    placeholder="Фамилия, имя, отчество, телефон, email, табельный номер"
                    @input="onRegistrySearchInput"
                ></b-form-input>
                <b-input-group-append v-if="filters.query">
                    <b-button
                        variant="outline-secondary"
                        title="Очистить поиск"
                        @click="clearRegistrySearch"
                    >
                        <i class="fas fa-times"></i>
                    </b-button>
                </b-input-group-append>
            </b-input-group>
            <b-form-select
                v-model="filters.department"
                :options="departmentOptions"
                size="sm"
            ></b-form-select>
            <b-form-select
                v-model="filters.citizenship"
                :options="citizenshipOptions"
                size="sm"
                aria-label="Фильтр по гражданству"
            ></b-form-select>
            <b-form-select
                v-model="filters.hiringForm"
                :options="hiringFormOptions"
                size="sm"
                aria-label="Фильтр по форме найма"
            ></b-form-select>
            <employee-multi-filter :skills.sync="filters.skills" :positions.sync="filters.positions"
                                   :itr.sync="filters.itr"
                                   :skill-options="skillOptions" :position-options="positionOptions"
                                   :skill-descriptions="skillDescriptions" />
            <b-form-select
                v-model="filters.priority"
                :options="priorityOptions"
                size="sm"
            ></b-form-select>
            <b-form-select
                v-model="filters.account"
                :options="accountOptions"
                size="sm"
            ></b-form-select>
        </section>

        <div
            v-if="activeFilterChips.length"
            class="employee-registry-active-filters"
        >
            <span class="employee-registry-active-filters__label">
                <i class="fas fa-filter" aria-hidden="true"></i>
                Включены фильтры:
            </span>
            <button
                v-for="chip in activeFilterChips"
                :key="chip.key"
                type="button"
                class="employee-registry-active-filter"
                :class="`employee-registry-active-filter--${chip.variant}`"
                :title="`Снять фильтр: ${chip.text}`"
                :disabled="loading"
                @click="clearFilterChip(chip.key)"
            >
                <i class="fas fa-check" aria-hidden="true"></i>
                <span class="employee-registry-active-filter__text">
                    {{ chip.text }}
                </span>
                <i
                    class="fas fa-times employee-registry-active-filter__remove"
                    aria-hidden="true"
                ></i>
            </button>
            <button
                type="button"
                class="employee-registry-active-filters__reset"
                :disabled="loading"
                @click="clearAllFilters"
            >
                Сбросить все
            </button>
            <span class="employee-registry-active-filters__count">
                Найдено: <strong>{{ filteredEmployees.length }}</strong>
                из {{ totals.all }}
            </span>
        </div>

        <b-alert v-if="errorMessage" show variant="danger">
            {{ errorMessage }}
            <b-button
                size="sm"
                variant="outline-danger"
                class="ml-2"
                @click="loadRegistry"
            >
                Повторить
            </b-button>
        </b-alert>

        <div v-if="loading" class="employee-registry-page__state">
            <b-spinner small class="mr-2"></b-spinner>
            Загружаю реестр
        </div>

        <template v-else>
            <div
                v-if="!activeFilterChips.length || selectedEmployeeIds.length"
                class="employee-registry-page__meta"
            >
                <span v-if="!activeFilterChips.length">
                    Найдено: <strong>{{ filteredEmployees.length }}</strong>
                    из {{ totals.all }}
                </span>
                <span v-if="selectedEmployeeIds.length">
                    Выбрано: <strong>{{ selectedEmployeeIds.length }}</strong>
                    <button type="button" @click="selectedEmployeeIds = []">
                        снять выбор
                    </button>
                </span>
            </div>

            <div class="employee-registry-table-wrap">
                <table class="employee-registry-table">
                    <thead>
                        <tr>
                            <th class="employee-registry-table__select">
                                <b-form-checkbox
                                    :checked="allVisibleSelected"
                                    :indeterminate="someVisibleSelected"
                                    aria-label="Выбрать текущую страницу"
                                    @change="toggleVisibleSelection"
                                ></b-form-checkbox>
                            </th>
                            <th>Сотрудник</th>
                            <th>Статус</th>
                            <th>Приоритет</th>
                            <th>Отдел</th>
                            <th>Должность</th>
                            <th>Контакты</th>
                            <th>Учётная запись</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="employee in pagedEmployees"
                            :key="employee.employee_id"
                            :data-employee-id="employee.employee_id"
                            :class="{
                                'employee-registry-highlight-target':
                                    isHighlightedEmployee(employee),
                                'employee-registry-no-card-access':
                                    employee.can_open_card !== true
                            }"
                            @click="openEmployee(employee)"
                        >
                            <td @click.stop>
                                <b-form-checkbox
                                    v-model="selectedEmployeeIds"
                                    :value="Number(employee.employee_id)"
                                    :aria-label="`Выбрать ${employee.display_name}`"
                                ></b-form-checkbox>
                            </td>
                            <td>
                                <div class="employee-registry-person">
                                    <EmployeeAvatar
                                        :avatar-url="employee.avatar_url"
                                        :name="employee.full_name || employee.display_name"
                                        :first-name="employee.first_name"
                                        :last-name="employee.last_name"
                                        :size="34"
                                    />
                                    <div>
                                        <div class="employee-registry-person__name">
                                            <EmployeeLink :employee-id="employee.employee_id" :user-id="employee.user_account && employee.user_account.user_id" :fallback="employee.display_name || employee.full_name" compact />
                                            <span v-if="employee.citizenship"
                                                  class="employee-registry-person__badge employee-registry-person__badge--citizenship"
                                                  :title="`Гражданство: ${employee.citizenship}`">
                                                {{ employee.citizenship }}
                                            </span>
                                            <span v-if="employee.hiring_form === 'gph'"
                                                  class="employee-registry-person__badge"
                                                  :title="`Форма найма: ${hiringFormLabel(employee.hiring_form)}`">
                                                {{ hiringFormLabel(employee.hiring_form) }}
                                            </span>
                                        </div>
                                        <small>#{{ employee.employee_id }}</small>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <b-badge :variant="statusVariant(employee.lifecycle_status)">
                                    {{ statusLabel(employee.lifecycle_status) }}
                                </b-badge>
                                <small v-if="employee.termination">
                                    с {{ formatDate(employee.termination.date) }}
                                </small>
                                <small
                                    v-if="employee.scheduled_termination"
                                    class="employee-registry-termination-plan"
                                >
                                    Увольнение {{ formatDate(
                                        employee.scheduled_termination.terminated_on
                                    ) }}
                                </small>
                            </td>
                            <td>{{ employee.reliability_priority || '—' }}</td>
                            <td>{{ employee.department_name || '—' }}</td>
                            <td>{{ employee.position || '—' }}</td>
                            <td>
                                <employee-registry-contacts :employee="employee" />
                            </td>
                            <td @click.stop>
                                <router-link
                                    v-if="employee.user_account"
                                    :to="`/users/${employee.user_account.user_id}`"
                                    :class="accountClass(employee.user_account)"
                                >
                                    {{ accountLabel(employee.user_account) }}
                                </router-link>
                                <router-link
                                    v-else-if="canOfferAccountCreation(employee)"
                                    :to="{path: '/users/create', query: {employee_id: employee.employee_id}}"
                                >
                                    {{ accountCreationLabel(employee) }}
                                </router-link>
                                <span v-else class="text-muted">Нет</span>
                            </td>
                            <td>
                                <i
                                    class="fas text-muted"
                                    :class="employee.can_open_card === true
                                        ? 'fa-chevron-right'
                                        : 'fa-lock'"
                                    :title="employee.can_open_card === true
                                        ? 'Открыть карточку'
                                        : 'Нет доступа к содержимому карточки'"
                                ></i>
                            </td>
                        </tr>
                        <tr v-if="!pagedEmployees.length">
                            <td colspan="9" class="employee-registry-table__empty">
                                Сотрудники не найдены
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="employee-registry-cards">
                <article
                    v-for="employee in pagedEmployees"
                    :key="employee.employee_id"
                    :data-employee-id="employee.employee_id"
                    :class="[
                        'employee-registry-card',
                        {
                            'employee-registry-highlight-target':
                                isHighlightedEmployee(employee),
                            'employee-registry-no-card-access':
                                employee.can_open_card !== true
                        }
                    ]"
                    @click="openEmployee(employee)"
                >
                    <div class="employee-registry-card__head">
                        <b-form-checkbox
                            v-model="selectedEmployeeIds"
                            :value="Number(employee.employee_id)"
                            @click.stop
                        ></b-form-checkbox>
                        <EmployeeAvatar
                            :avatar-url="employee.avatar_url"
                            :name="employee.full_name || employee.display_name"
                            :first-name="employee.first_name"
                            :last-name="employee.last_name"
                            :size="38"
                        />
                        <div>
                            <div class="employee-registry-person__name">
                                <EmployeeLink :employee-id="employee.employee_id" :user-id="employee.user_account && employee.user_account.user_id" :fallback="employee.display_name || employee.full_name" compact />
                                <span v-if="employee.citizenship"
                                      class="employee-registry-person__badge employee-registry-person__badge--citizenship"
                                      :title="`Гражданство: ${employee.citizenship}`">
                                    {{ employee.citizenship }}
                                </span>
                                <span v-if="employee.hiring_form === 'gph'"
                                      class="employee-registry-person__badge"
                                      :title="`Форма найма: ${hiringFormLabel(employee.hiring_form)}`">
                                    {{ hiringFormLabel(employee.hiring_form) }}
                                </span>
                            </div>
                            <small>#{{ employee.employee_id }}</small>
                        </div>
                        <div class="employee-registry-card__statuses">
                            <b-badge :variant="statusVariant(employee.lifecycle_status)">
                                {{ statusLabel(employee.lifecycle_status) }}
                            </b-badge>
                            <b-badge
                                v-if="employee.can_open_card !== true"
                                variant="light"
                                title="Нет доступа к содержимому карточки"
                            >
                                <i class="fas fa-lock mr-1" aria-hidden="true"></i>
                                Карточка закрыта
                            </b-badge>
                            <b-badge
                                v-if="employee.scheduled_termination"
                                variant="warning"
                                :title="`Последний рабочий день: ${formatDate(
                                    employee.scheduled_termination.terminated_on
                                )}`"
                            >
                                Увольнение {{ formatDate(
                                    employee.scheduled_termination.terminated_on
                                ) }}
                            </b-badge>
                        </div>
                    </div>
                    <dl>
                        <div>
                            <dt>Отдел</dt>
                            <dd>{{ employee.department_name || '—' }}</dd>
                        </div>
                        <div>
                            <dt>Должность</dt>
                            <dd>{{ employee.position || '—' }}</dd>
                        </div>
                        <div>
                            <dt>Приоритет</dt>
                            <dd>{{ employee.reliability_priority || '—' }}</dd>
                        </div>
                        <div class="employee-registry-card__contacts">
                            <dt>Контакты</dt>
                            <dd><employee-registry-contacts :employee="employee" /></dd>
                        </div>
                        <div>
                            <dt>Учётная запись</dt>
                            <dd @click.stop>
                                <router-link
                                    v-if="employee.user_account"
                                    :to="`/users/${employee.user_account.user_id}`"
                                    :class="accountClass(employee.user_account)"
                                >
                                    {{ accountLabel(employee.user_account) }}
                                </router-link>
                                <router-link
                                    v-else-if="canOfferAccountCreation(employee)"
                                    :to="{path: '/users/create', query: {employee_id: employee.employee_id}}"
                                >
                                    {{ accountCreationLabel(employee) }}
                                </router-link>
                                <span v-else>Нет</span>
                            </dd>
                        </div>
                    </dl>
                </article>
            </div>

            <footer class="employee-registry-page__footer">
                <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredEmployees.length"
                    :per-page="perPage"
                    size="sm"
                    first-number
                    last-number
                    class="mb-0"
                ></b-pagination>
                <b-form-select
                    v-model="perPage"
                    :options="[50, 100, 200]"
                    size="sm"
                    class="employee-registry-page__page-size"
                ></b-form-select>
            </footer>
        </template>

        <GoogleContactsImportModal
            :visible="showImportModal"
            :file="googleContactsFile"
            :employees="googleContactsEmployeeOptions"
            @close="closeImportModal"
            @applied="onImportApplied"
        />

        <employee-create-modal
            v-model="createEmployeeVisible"
            @created="onEmployeeCreated"
        ></employee-create-modal>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import EmployeeMultiFilter from './EmployeeMultiFilter.vue';
import {employeeFilterOptions, matchesEmployeeFilters, employeeSkillDescriptions} from './employeeRegistryFilters';
import {registrySearchWords, employeeRegistryMatchesSearch} from './employeeRegistrySearch';
import {hiringFormOptions, hiringFormLabel, matchesHiringForm} from './employeeHiringForm';
import api from '@/utils/api';
import {returnToRow} from '@/utils/rowHighlight';
import EmployeeRegistryContacts from './EmployeeRegistryContacts.vue';
import EmployeeAvatar from '@/components/EmployeeAvatar.vue';
import EmployeeCreateModal from './EmployeeCreateModal.vue';
import GoogleContactsImportModal from './GoogleContactsImportModal.vue';

const SESSION_KEY = 'employeeRegistryReturn';
const SESSION_TTL_MS = 20 * 60 * 1000;
const HIGHLIGHT_CLEANUP_DELAY_MS = 4500;


const EMPTY_TOTALS = {
    all: 0,
    active: 0,
    reserve: 0,
    unverified: 0
};

function cloneJson(value) {
    return JSON.parse(JSON.stringify(value || {}));
}

function emptyRegistryFilters() {
    return {
        query: '',
        status: 'active',
        department: '',
        hiringForm: '',
        skills: [],
        itr: false,
        positions: [],
        citizenship: '',
        priority: '',
        account: ''
    };
}

export default {
    name: 'EmployeeRegistry',
    components: {EmployeeLink,
        EmployeeRegistryContacts,
        EmployeeMultiFilter,
        EmployeeAvatar,
        EmployeeCreateModal,
        GoogleContactsImportModal
    },
    data() {
        return {
            employees: [],
            mergeReviewCount: null,
            mergeReviewCountError: false,
            mergeReviewCountLoading: false,
            mergeReviewTimer: null,
            totals: Object.assign({}, EMPTY_TOTALS),
            permissions: {
                can_view_full_registry: false,
                can_import: false,
                can_manage_employees: false,
                can_settings: false
            },
            filters: emptyRegistryFilters(),
            statusQuickFilters: [
                {value: 'active', text: 'Действующие', totalKey: 'active'},
                {value: 'reserve', text: 'Резерв', totalKey: 'reserve'},
                {value: 'unverified', text: 'Непроверенные', totalKey: 'unverified'},
                {value: '', text: 'Все', totalKey: 'all'}
            ],
            accountOptions: [
                {value: '', text: 'Любая учётная запись'},
                {value: 'none', text: 'Без учётной записи'},
                {value: 'active', text: 'Учётная запись активна'},
                {value: 'disabled', text: 'Учётная запись отключена'}
            ],
            loading: false,
            exporting: false,
            errorMessage: '',
            selectedEmployeeIds: [],
            currentPage: 1,
            perPage: 100,
            highlightedEmployeeId: null,
            highlightCleanupTimer: null,
            restoringListContext: false,
            registryRequestSerial: 0,
            searchDebounceTimer: null,
            searchModeActive: false,
            statusBeforeSearch: 'active',
            googleContactsFile: null,
            importEmployees: [],
            importEmployeesLoading: false,
            showImportModal: false,
            createEmployeeVisible: false
        };
    },
    computed: {
        skillOptions() { return employeeFilterOptions(this.employees, 'skills'); },
        skillDescriptions() { return employeeSkillDescriptions(this.employees); },
        positionOptions() { return employeeFilterOptions(this.employees, 'positions'); },
        hiringFormOptions() { return hiringFormOptions; },
        departmentOptions() {
            const names = Array.from(new Set(
                this.employees.map(employee => employee.department_name).filter(Boolean)
            )).sort((left, right) => left.localeCompare(right, 'ru'));
            return [{value: '', text: 'Все отделы'}].concat(
                names.map(name => ({value: name, text: name}))
            );
        },
        citizenshipOptions() {
            const values = Array.from(new Set(this.employees.map(employee => (
                String(employee.citizenship || '').trim()
            )).filter(Boolean))).sort((left, right) => left.localeCompare(right, 'ru'));
            return [
                {value: '', text: 'Любое гражданство'},
                {value: '__none__', text: 'Гражданство не указано'}
            ].concat(values.map(value => ({value, text: value})));
        },
        googleContactsEmployeeOptions() {
            return this.importEmployees.length
                ? this.importEmployees
                : this.employees;
        },
        priorityOptions() {
            const values = Array.from(new Set(
                this.employees
                    .map(employee => employee.reliability_priority)
                    .filter(Boolean)
            )).sort((left, right) => Number(left) - Number(right));
            return [
                {value: '', text: 'Все приоритеты'},
                {value: '__none__', text: 'Без приоритета'}
            ].concat(values.map(value => ({
                value: String(value),
                text: `Приоритет ${value}`
            })));
        },
        filteredEmployees() {
            const words = registrySearchWords(this.filters.query);
            return this.employees.filter(employee => {
                if (!matchesEmployeeFilters(employee, this.filters.skills, this.filters.positions, this.filters.itr)) return false;
                if (
                    this.filters.status
                    && employee.lifecycle_status !== this.filters.status
                ) return false;
                if (
                    this.filters.department
                    && employee.department_name !== this.filters.department
                ) return false;
                if (!matchesHiringForm(employee, this.filters.hiringForm)) return false;
                if (this.filters.citizenship) {
                    const citizenship = String(employee.citizenship || '').trim();
                    if (
                        this.filters.citizenship === '__none__'
                            ? Boolean(citizenship)
                            : citizenship !== this.filters.citizenship
                    ) return false;
                }
                if (
                    this.filters.priority === '__none__'
                    && employee.reliability_priority
                ) return false;
                if (
                    this.filters.priority
                    && this.filters.priority !== '__none__'
                    && String(employee.reliability_priority || '')
                        !== String(this.filters.priority)
                ) return false;
                if (!this.matchesAccountFilter(employee)) return false;
                return employeeRegistryMatchesSearch(employee, words);
            }).slice().sort((left, right) => (
                String(left.display_name || '').localeCompare(
                    String(right.display_name || ''),
                    'ru'
                )
            ));
        },
        pagedEmployees() {
            const offset = (this.currentPage - 1) * this.perPage;
            return this.filteredEmployees.slice(offset, offset + this.perPage);
        },
        filteredEmployeeIds() {
            return this.filteredEmployees.map(employee => Number(employee.employee_id));
        },
        visibleEmployeeIds() {
            return this.pagedEmployees.map(employee => Number(employee.employee_id));
        },
        allVisibleSelected() {
            return Boolean(this.visibleEmployeeIds.length)
                && this.visibleEmployeeIds.every(id => (
                    this.selectedEmployeeIds.indexOf(id) !== -1
                ));
        },
        someVisibleSelected() {
            return !this.allVisibleSelected && this.visibleEmployeeIds.some(id => (
                this.selectedEmployeeIds.indexOf(id) !== -1
            ));
        },
        activeStatusOption() {
            return this.statusQuickFilters.find(option => (
                String(option.value) === String(this.filters.status)
            )) || null;
        },
        activePriorityOption() {
            return this.priorityOptions.find(option => (
                String(option.value) === String(this.filters.priority)
            )) || null;
        },
        activeCitizenshipOption() {
            return this.citizenshipOptions.find(option => (
                String(option.value) === String(this.filters.citizenship)
            )) || null;
        },
        activeAccountOption() {
            return this.accountOptions.find(option => (
                String(option.value) === String(this.filters.account)
            )) || null;
        },
        activeFilterChips() {
            const chips = [];
            if (this.filters.skills.length) chips.push({key: 'skills', text: 'Навыки: ' + this.filters.skills.join(', '), variant: 'info'});
            if (this.filters.itr) chips.push({key: 'itr', text: 'ИТР', variant: 'info'});
            if (this.filters.positions.length) chips.push({key: 'positions', text: 'Должности: ' + this.filters.positions.join(', '), variant: 'info'});
            if (this.filters.hiringForm) chips.push({key: 'hiringForm', text: 'Форма найма: ' + hiringFormLabel(this.filters.hiringForm), variant: 'secondary'});
            if (this.permissions.can_view_full_registry && this.filters.status && this.activeStatusOption) {
                chips.push({
                    key: 'status',
                    text: `Статус: ${this.activeStatusOption.text}`,
                    variant: 'success'
                });
            }
            const query = String(this.filters.query || '').trim();
            if (query) {
                chips.push({
                    key: 'search',
                    text: `Поиск: ${query}`,
                    variant: 'primary'
                });
            }
            if (this.filters.department) {
                chips.push({
                    key: 'department',
                    text: `Отдел: ${this.filters.department}`,
                    variant: 'info'
                });
            }
            if (this.filters.citizenship && this.activeCitizenshipOption) {
                chips.push({
                    key: 'citizenship',
                    text: this.activeCitizenshipOption.text,
                    variant: 'warning'
                });
            }
            if (this.filters.priority && this.activePriorityOption) {
                chips.push({
                    key: 'priority',
                    text: this.activePriorityOption.text,
                    variant: 'secondary'
                });
            }
            if (this.filters.account && this.activeAccountOption) {
                chips.push({
                    key: 'account',
                    text: this.activeAccountOption.text,
                    variant: 'warning'
                });
            }
            return chips;
        }
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                if (!this.restoringListContext) {
                    this.currentPage = 1;
                }
            }
        },
        perPage() {
            if (!this.restoringListContext) {
                this.currentPage = 1;
            }
        }
    },
    beforeDestroy() {
        if (this._cancelRegistryHighlight) this._cancelRegistryHighlight();
        clearInterval(this.mergeReviewTimer);
        window.removeEventListener('focus', this.loadMergeReviewCount);
        document.removeEventListener('visibilitychange', this.loadMergeReviewCount);
        this.clearHighlightCleanupTimer();
        this.clearSearchDebounceTimer();
    },
    mounted() {
        this.mergeReviewTimer = setInterval(this.loadMergeReviewCount, 60000);
        window.addEventListener('focus', this.loadMergeReviewCount);
        document.addEventListener('visibilitychange', this.loadMergeReviewCount);
        this.$store.dispatch('getDepartments').catch(() => {});
        this.initializeRegistry();
    },
    methods: {
        hiringFormLabel,
        async loadMergeReviewCount() {
            if (!this.permissions.can_manage_employees) { this.mergeReviewCount = null; return; }
            if (this._isDestroyed || document.hidden || this.mergeReviewCountLoading) return;
            this.mergeReviewCountLoading = true;
            try {
                const response = await api.get('/api/employees/merge-cases/count', {ignoreModal: true});
                if (this._isDestroyed || !this.permissions.can_manage_employees) return;
                this.mergeReviewCount = Number(response.data.count) || 0;
                this.mergeReviewCountError = false;
            } catch (error) {
                if (!this._isDestroyed) this.mergeReviewCountError = true;
            } finally {
                this.mergeReviewCountLoading = false;
            }
        },
        async initializeRegistry() {
            const highlight = this.$route.query && this.$route.query.highlight;
            const employeeId = Number(highlight);
            if (Number.isInteger(employeeId) && employeeId > 0) {
                const session = this.readReturnSession(employeeId);
                if (session) {
                    this.clearReturnSession();
                    this.applyListContext(session);
                    await this.$nextTick();
                } else {
                    this.restoringListContext = true;
                    this.filters = Object.assign(emptyRegistryFilters(), {
                        query: String(employeeId),
                        status: ''
                    });
                    this.searchModeActive = true;
                    this.statusBeforeSearch = 'active';
                    await this.$nextTick();
                    this.restoringListContext = false;
                }
            }
            await this.loadRegistry();
            if (
                this.$route.query &&
                this.$route.query.create === '1' &&
                this.permissions.can_manage_employees
            ) {
                this.openCreateEmployee();
                this.consumeCreateEmployeeRouteFlag();
            }
            if (highlight) {
                await this.scrollToHighlight(highlight);
                this.scheduleHighlightCleanup(highlight);
            }
        },
        async loadRegistry() {
            const requestSerial = ++this.registryRequestSerial;
            this.loading = true;
            this.errorMessage = '';
            try {
                if ((this.$store.state.employeeDirectorySettings || {}).can_employee_registry === false) {
                    this.filters.status = 'active';
                }
                const query = String(this.filters.query || '').trim();
                const params = {};
                if (this.filters.status) params.status = this.filters.status;
                if (query) params.q = query;
                const response = await api.get('/api/employees', {
                    params,
                    ignoreModal: true
                });
                if (requestSerial !== this.registryRequestSerial) return;
                const data = response && response.data || {};
                if (data.permissions && !data.permissions.can_view_full_registry) this.filters.status = 'active';
                this.employees = Array.isArray(data.employees)
                    ? data.employees
                    : [];
                this.totals = Object.assign({}, EMPTY_TOTALS, data.totals || {});
                this.permissions = Object.assign(
                    {},
                    this.permissions,
                    data.permissions || {}
                );
                this.loadMergeReviewCount();
                this.selectedEmployeeIds = this.selectedEmployeeIds.filter(id => (
                    this.employees.some(employee => Number(employee.employee_id) === Number(id))
                ));
            } catch (error) {
                if (requestSerial !== this.registryRequestSerial) return;
                if (error.response && error.response.status === 403 && this.filters.status !== 'active'
                    && error.response.data && error.response.data.code === 'EMPLOYEE_REGISTRY_SCOPE_FORBIDDEN') {
                    this.filters.status = 'active';
                    return this.loadRegistry();
                }
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось загрузить реестр сотрудников.'
                );
            } finally {
                if (requestSerial === this.registryRequestSerial) {
                    this.loading = false;
                }
            }
        },
        applyListContext(context) {
            if (!context) return;
            this.restoringListContext = true;
            this.filters = Object.assign(
                emptyRegistryFilters(),
                cloneJson(context.filters)
            );
            this.perPage = Number(context.perPage) || 100;
            this.currentPage = Math.max(1, Number(context.page) || 1);
            this.searchModeActive = Boolean(this.filters.query);
            this.statusBeforeSearch = context.statusBeforeSearch || 'active';
            this.$nextTick(() => {
                if (!this._isDestroyed) {
                    this.restoringListContext = false;
                }
            });
        },
        saveReturnSession(employeeId) {
            try {
                sessionStorage.setItem(SESSION_KEY, JSON.stringify({
                    employeeId: Number(employeeId),
                    page: this.currentPage,
                    perPage: this.perPage,
                    filters: cloneJson(this.filters),
                    statusBeforeSearch: this.statusBeforeSearch,
                    savedAt: Date.now()
                }));
            } catch (error) { /* ignore */ }
        },
        readReturnSession(employeeId) {
            try {
                const raw = sessionStorage.getItem(SESSION_KEY);
                if (!raw) return null;
                const data = JSON.parse(raw);
                if (
                    Number(data.employeeId) === Number(employeeId)
                    && Date.now() - Number(data.savedAt || 0) < SESSION_TTL_MS
                ) {
                    return data;
                }
            } catch (error) { /* ignore */ }
            return null;
        },
        clearReturnSession() {
            try {
                sessionStorage.removeItem(SESSION_KEY);
            } catch (error) { /* ignore */ }
        },
        clearHighlightCleanupTimer() {
            if (!this.highlightCleanupTimer) return;
            clearTimeout(this.highlightCleanupTimer);
            this.highlightCleanupTimer = null;
        },
        clearSearchDebounceTimer() {
            if (!this.searchDebounceTimer) return;
            clearTimeout(this.searchDebounceTimer);
            this.searchDebounceTimer = null;
        },
        scheduleRegistryReload(delay) {
            this.clearSearchDebounceTimer();
            this.registryRequestSerial += 1;
            this.searchDebounceTimer = setTimeout(() => {
                this.searchDebounceTimer = null;
                if (!this._isDestroyed) {
                    this.loadRegistry();
                }
            }, Math.max(0, Number(delay) || 0));
        },
        onRegistrySearchInput(value) {
            const hasQuery = Boolean(String(value || '').trim());
            if (hasQuery && !this.searchModeActive) {
                this.statusBeforeSearch = this.filters.status;
                this.searchModeActive = true;
                this.filters.status = '';
            } else if (!hasQuery && this.searchModeActive) {
                this.searchModeActive = false;
                this.filters.status = this.statusBeforeSearch;
            }
            this.scheduleRegistryReload(hasQuery ? 350 : 0);
        },
        clearRegistrySearch() {
            this.filters.query = '';
            this.onRegistrySearchInput('');
        },
        applyStatusFilter(status) {
            this.filters.status = status;
            if (this.searchModeActive) {
                this.statusBeforeSearch = status;
            }
            this.scheduleRegistryReload(0);
        },
        scheduleHighlightCleanup(employeeId) {
            this.clearHighlightCleanupTimer();
            this.highlightCleanupTimer = setTimeout(() => {
                if (this._isDestroyed) return;
                this.highlightedEmployeeId = null;
                const currentHighlight = this.$route.query
                    && this.$route.query.highlight;
                if (String(currentHighlight || '') !== String(employeeId)) return;
                const nextQuery = Object.assign({}, this.$route.query);
                delete nextQuery.highlight;
                this.$router.replace({
                    path: this.$route.path,
                    query: nextQuery
                }).catch(() => {});
            }, HIGHLIGHT_CLEANUP_DELAY_MS);
        },
        isHighlightedEmployee(employee) {
            return Boolean(
                employee
                && this.highlightedEmployeeId
                && Number(employee.employee_id)
                    === Number(this.highlightedEmployeeId)
            );
        },
        ensureHighlightVisible(employeeId) {
            let index = this.filteredEmployees.findIndex(employee => (
                Number(employee.employee_id) === Number(employeeId)
            ));
            if (index < 0) {
                const employee = this.employees.find(item => (
                    Number(item.employee_id) === Number(employeeId)
                ));
                if (!employee) return false;
                this.restoringListContext = true;
                this.filters = Object.assign(emptyRegistryFilters(), {
                    status: employee.lifecycle_status || ''
                });
                this.$nextTick(() => {
                    if (!this._isDestroyed) {
                        this.restoringListContext = false;
                    }
                });
                index = this.filteredEmployees.findIndex(item => (
                    Number(item.employee_id) === Number(employeeId)
                ));
            }
            if (index < 0) return false;
            this.currentPage = Math.floor(index / this.perPage) + 1;
            return true;
        },
        resolveHighlightTarget(employeeId) {
            if (!this.$el) return null;
            const targets = Array.prototype.slice.call(
                this.$el.querySelectorAll(
                    `[data-employee-id="${Number(employeeId)}"]`
                )
            );
            return targets.find(target => target.getClientRects().length) || null;
        },
        async scrollToHighlight(employeeId) {
            if (this._cancelRegistryHighlight) this._cancelRegistryHighlight();
            this.highlightedEmployeeId = Number(employeeId);
            if (!this.ensureHighlightVisible(employeeId)) return;
            await this.$nextTick();
            await this.$nextTick();
            if (this._isDestroyed || this.highlightedEmployeeId !== Number(employeeId)) return;
            this._cancelRegistryHighlight = returnToRow(() => this.resolveHighlightTarget(employeeId), {
                container: target => {
                    const body = target.closest('.employee-registry-table-wrap');
                    return body && body.scrollHeight > body.clientHeight ? body : null;
                }
            });
        },
        matchesAccountFilter(employee) {
            if (!this.filters.account) return true;
            const account = employee.user_account;
            if (this.filters.account === 'none') return !account;
            if (!account) return false;
            if (this.filters.account === 'active') return account.active === true;
            return account.active !== true;
        },
        clearFilterChip(key) {
            if (key === 'status') {
                this.filters.status = '';
                if (this.searchModeActive) this.statusBeforeSearch = '';
                this.scheduleRegistryReload(0);
            } else if (key === 'search') {
                this.clearRegistrySearch();
            } else if (key === 'department') {
                this.filters.department = '';
            } else if (key === 'hiringForm') {
                this.filters.hiringForm = '';
            } else if (key === 'citizenship') {
                this.filters.citizenship = '';
            } else if (key === 'priority') {
                this.filters.priority = '';
            } else if (key === 'account') {
                this.filters.account = '';
            } else if (key === 'itr') {
                this.filters.itr = false;
            } else if (key === 'skills' || key === 'positions') {
                this.filters[key] = [];
            }
        },
        clearAllFilters() {
            const needsReload = Boolean(this.filters.query || this.filters.status);
            this.clearSearchDebounceTimer();
            this.searchModeActive = false;
            this.statusBeforeSearch = '';
            this.filters = Object.assign(emptyRegistryFilters(), {status: ''});
            if (needsReload) this.scheduleRegistryReload(0);
        },
        toggleVisibleSelection(value) {
            const selected = new Set(this.selectedEmployeeIds);
            this.visibleEmployeeIds.forEach(id => {
                if (value) selected.add(id);
                else selected.delete(id);
            });
            this.selectedEmployeeIds = Array.from(selected);
        },
        async openEmployee(employee) {
            if (
                !employee
                || !employee.employee_id
                || employee.can_open_card !== true
            ) return;
            const employeeId = Number(employee.employee_id);
            this.saveReturnSession(employeeId);
            const nextQuery = Object.assign(
                {},
                this.$route.query,
                {highlight: String(employeeId)}
            );
            await this.$router.replace({
                path: this.$route.path,
                query: nextQuery
            }).catch(() => {});
            return this.$router.push({
                name: 'employee-details',
                params: {id: String(employeeId)}
            }).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') {
                    throw error;
                }
            });
        },
        statusLabel(status) {
            return {
                active: 'Действующий',
                reserve: 'Резерв',
                unverified: 'Непроверенный'
            }[status] || status || 'Не указан';
        },
        statusVariant(status) {
            if (status === 'active') return 'success';
            if (status === 'unverified') return 'warning';
            return 'secondary';
        },
        accountLabel(account) {
            return account && account.active === true ? 'Активна' : 'Отключена';
        },
        accountClass(account) {
            return account && account.active === true
                ? 'text-success'
                : 'text-secondary';
        },
        canOfferAccountCreation(employee) {
            if (!employee || employee.user_account) return false;
            if (employee.lifecycle_status === 'active') return true;
            return employee.lifecycle_status === 'unverified'
                && this.$store.getters.isAdmin === true;
        },
        accountCreationLabel(employee) {
            return employee && employee.lifecycle_status === 'unverified'
                ? 'Подтвердить и создать'
                : 'Создать';
        },
        formatDate(value) {
            if (!value) return '—';
            const match = String(value).slice(0, 10).match(
                /^(\d{4})-(\d{2})-(\d{2})$/
            );
            return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
        },
        async onGoogleContactsFileSelected(event) {
            const file = event && event.target && event.target.files
                ? event.target.files[0]
                : null;
            if (!file) return;
            this.googleContactsFile = file;
            event.target.value = '';
            this.importEmployeesLoading = true;
            try {
                const response = await api.get('/api/employees', {
                    ignoreModal: true
                });
                const data = response && response.data || {};
                this.importEmployees = Array.isArray(data.employees)
                    ? data.employees
                    : [];
                this.showImportModal = true;
            } catch (error) {
                this.googleContactsFile = null;
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось загрузить сотрудников для сопоставления импорта.'
                );
            } finally {
                this.importEmployeesLoading = false;
            }
        },
        closeImportModal() {
            this.showImportModal = false;
            this.googleContactsFile = null;
        },
        async onImportApplied() {
            await this.loadRegistry();
        },
        openCreateEmployee() {
            this.createEmployeeVisible = true;
        },
        consumeCreateEmployeeRouteFlag() {
            const query = Object.assign({}, this.$route.query || {});
            delete query.create;
            this.$router.replace({name: 'employees', query}).catch(error => {
                if (!error || error.name !== 'NavigationDuplicated') throw error;
            });
        },
        async onEmployeeCreated(employee) {
            await this.loadRegistry();
            const employeeId = Number(employee && employee.employee_id);
            if (employeeId) {
                await this.$router.push(`/employees/${employeeId}`);
            }
        },
        async exportEmployees(employeeIds) {
            if (this.exporting || !employeeIds.length) return;
            this.exporting = true;
            this.errorMessage = '';
            try {
                const response = await api.get(
                    '/api/employees/export/google-contacts.csv',
                    {
                        params: {ids: employeeIds.join(',')},
                        responseType: 'blob',
                        ignoreModal: true
                    }
                );
                const url = window.URL.createObjectURL(response.data);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Сотрудники Аргус ${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось экспортировать сотрудников.'
                );
            } finally {
                this.exporting = false;
            }
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object' && !(data instanceof Blob)) {
                return data.msg || data.message || fallback;
            }
            return typeof data === 'string' && data ? data : fallback;
        }
    }
};
</script>

<style src="@/assets/rowHighlight.css"></style>
<style scoped>
.employee-registry-page {
    padding: 12px 18px 18px;
    min-height: 100%;
    background: #f4f7fa;
}

.employee-registry-page__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 8px;
}

.employee-registry-page__header h1 {
    margin: 0;
    color: #24364b;
    font-size: 1.45rem;
}

.employee-registry-page__header p {
    display: none;
    margin: 4px 0 0;
    color: #6a7b8d;
    font-size: 0.88rem;
}

.employee-registry-page__actions,
.employee-registry-page__summary,
.employee-registry-page__filters,
.employee-registry-page__meta,
.employee-registry-page__footer {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.employee-registry-page__summary {
    margin-bottom: 7px;
}

.employee-registry-status {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 9px;
    border: 1px solid #d8e2ec;
    border-radius: 9px;
    background: #fff;
    color: #53677b;
}

.employee-registry-status--active {
    border-color: #2d72d9;
    background: #eaf3ff;
    color: #1454af;
    box-shadow: 0 0 0 1px rgba(45, 114, 217, 0.12);
}

.employee-registry-status strong {
    min-width: 28px;
    padding: 1px 7px;
    border-radius: 999px;
    background: rgba(31, 67, 102, 0.09);
    text-align: center;
}

.employee-registry-page__filters {
    padding: 7px;
    margin-bottom: 7px;
    border: 1px solid #d9e3ec;
    border-radius: 10px;
    background: #fff;
}

.employee-registry-page__search {
    flex: 1 1 360px;
}

.employee-registry-page__filters .custom-select {
    flex: 0 1 210px;
}

.employee-registry-active-filters {
    position: sticky;
    z-index: 25;
    top: calc(var(--shell-header-height, 64px) + 5px);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 7px;
    padding: 0.35rem 0.5rem;
    border: 1px solid #ffe08a;
    border-radius: 8px;
    background: #fff8e1;
    color: #5c4300;
    font-size: 0.84rem;
    box-shadow: 0 2px 6px rgba(115, 83, 0, 0.06);
    backdrop-filter: blur(10px);
}

.employee-registry-active-filters__label {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.3rem;
    font-weight: 700;
}

.employee-registry-active-filter {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    max-width: 100%;
    min-height: 28px;
    padding: 0.2rem 0.45rem;
    border: 1px solid transparent;
    border-radius: 999px;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
}

.employee-registry-active-filter:hover:not(:disabled),
.employee-registry-active-filter:focus:not(:disabled) {
    filter: brightness(0.94);
}

.employee-registry-active-filter:disabled,
.employee-registry-active-filters__reset:disabled {
    cursor: wait;
    opacity: 0.65;
}

.employee-registry-active-filter__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.employee-registry-active-filter__remove {
    margin-left: 0.1rem;
    opacity: 0.85;
}

.employee-registry-active-filter--primary {
    border-color: #007bff;
    background: #007bff;
}

.employee-registry-active-filter--secondary {
    border-color: #6c757d;
    background: #6c757d;
}

.employee-registry-active-filter--warning {
    border-color: #ffc107;
    background: #ffc107;
    color: #212529;
}

.employee-registry-active-filter--info {
    border-color: #17a2b8;
    background: #17a2b8;
}

.employee-registry-active-filter--success {
    border-color: #218838;
    background: #218838;
}

.employee-registry-active-filters__reset {
    flex: 0 0 auto;
    min-height: 28px;
    padding: 0.2rem 0.5rem;
    border: 1px solid #b38300;
    border-radius: 0.25rem;
    background: #fff;
    color: #5c4300;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
}

.employee-registry-active-filters__reset:hover:not(:disabled),
.employee-registry-active-filters__reset:focus:not(:disabled) {
    background: #fff3bf;
}

.employee-registry-active-filters__count {
    flex: 0 0 auto;
    margin-left: auto;
    color: #6b5720;
    font-size: 0.78rem;
    white-space: nowrap;
}

.employee-registry-page__state {
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6a7b8d;
}

.employee-registry-page__meta {
    justify-content: space-between;
    min-height: 30px;
    margin-bottom: 6px;
    color: #617386;
    font-size: 0.84rem;
}

.employee-registry-page__meta button {
    padding: 0;
    border: 0;
    background: transparent;
    color: #1668c7;
}

.employee-registry-table-wrap {
    overflow-x: auto;
    max-height: none;
    border: 1px solid #d5e0ea;
    border-radius: 10px;
    background: #fff;
}

.employee-registry-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 0.82rem;
}

.employee-registry-table th {
    position: static;
    padding: 9px 8px;
    border-bottom: 1px solid #cad7e3;
    background: #e9eff5;
    color: #40556a;
    white-space: nowrap;
}

.employee-registry-table td {
    padding: 7px 8px;
    border-bottom: 1px solid #e5ebf1;
    vertical-align: middle;
}

.employee-registry-table tbody tr {
    cursor: pointer;
}

.employee-registry-table tbody tr:hover {
    background: #f1f7ff;
}

.employee-registry-table tbody tr.employee-registry-no-card-access,
.employee-registry-card.employee-registry-no-card-access {
    cursor: default;
}

.employee-registry-table tbody tr.employee-registry-no-card-access:hover {
    background: inherit;
}

.employee-registry-table tbody tr.employee-registry-highlight-target > td,
.employee-registry-card.employee-registry-highlight-target {
    background: #fff3cd;
}

.employee-registry-table td strong,
.employee-registry-table td small {
    display: block;
}

.employee-registry-table td small {
    margin-top: 2px;
    color: #718194;
}

.employee-registry-table__select {
    width: 40px;
}

.employee-registry-person {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 180px;
}

.employee-registry-person > div {
    min-width: 0;
}

.employee-registry-page__filters .employee-multi-filter {
    flex: 0 1 290px;
}

.employee-registry-person__name {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 6px;
}

.employee-registry-person__badge {
    display: inline-block;
    padding: 1px 5px;
    border-radius: 4px;
    background: #edf0f4;
    color: #4a5568;
    font-size: 0.7rem;
    font-weight: 600;
    line-height: 1.3;
    white-space: nowrap;
}

.employee-registry-person__badge--citizenship {
    background: #e7f1fc;
    color: #32658f;
}

.employee-registry-termination-plan {
    display: block;
    margin-top: 4px;
    color: #96640d;
    font-weight: 600;
}

.employee-registry-table__empty {
    height: 120px;
    color: #748496;
    text-align: center;
}

.employee-registry-cards {
    display: none;
}

.employee-registry-page__footer {
    justify-content: flex-end;
    margin-top: 10px;
}

.employee-registry-page__page-size {
    width: 85px;
}

@media (max-width: 767px) {
    .employee-registry-page {
        padding: 10px;
    }

    .employee-registry-page__header {
        display: block;
    }

    .employee-registry-page__actions {
        margin-top: 10px;
    }

    .employee-registry-page__filters .custom-select,
    .employee-registry-page__filters .employee-multi-filter {
        flex: 1 1 calc(50% - 8px);
    }

    .employee-registry-page__filters .employee-multi-filter {
        flex-basis: 100%;
    }

    .employee-registry-active-filters {
        align-items: stretch;
    }

    .employee-registry-active-filters__label {
        flex: 1 0 100%;
    }

    .employee-registry-active-filter {
        max-width: calc(100vw - 40px);
    }

    .employee-registry-table-wrap {
        display: none;
    }

    .employee-registry-cards {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 8px;
    }

    .employee-registry-card {
        min-width: 0;
        padding: 11px;
        border: 1px solid #d5e0ea;
        border-radius: 10px;
        background: #fff;
    }

    .employee-registry-card__head {
        display: grid;
        grid-template-columns: auto auto minmax(0, 1fr);
        align-items: start;
        gap: 8px;
    }

    .employee-registry-card__head > div {
        min-width: 0;
    }

    .employee-registry-card__statuses {
        grid-column: 2 / -1;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
    }

    .employee-registry-card__statuses .badge {
        max-width: 100%;
        white-space: normal;
        overflow-wrap: anywhere;
        text-align: left;
    }

    .employee-registry-card__head strong,
    .employee-registry-card__head small {
        display: block;
    }

    .employee-registry-card__head small {
        color: #748496;
    }

    .employee-registry-card dl {
        display: grid;
        gap: 5px;
        margin: 10px 0 0 28px;
    }

    .employee-registry-card dl > div {
        display: grid;
        grid-template-columns: 90px minmax(0, 1fr);
        gap: 8px;
    }

    .employee-registry-card dt {
        color: #748496;
        font-weight: 400;
    }

    .employee-registry-card dl .employee-registry-card__contacts {
        grid-template-columns: minmax(0, 1fr);
        gap: 3px;
    }

    .employee-registry-card dd {
        min-width: 0;
        overflow-wrap: anywhere;
        margin: 0;
    }
}
</style>
